<script setup>
import { computed, onBeforeUnmount, onMounted, ref } from 'vue';
import ResourceMonitorAPI from 'dashboard/api/resourceMonitor';
import Button from 'dashboard/components-next/button/Button.vue';

const metrics = ref(null);
const loading = ref(true);
const error = ref('');
const speedTesting = ref(false);
const speedResult = ref(null);
const speedModalOpen = ref(false);
const speedStage = ref('Ready');
const speedProgress = ref(0);
const liveGauge = ref(0);
const networkRates = ref({ down: 0, up: 0 });
let previousNetwork;
let refreshTimer;

const formatBytes = bytes => {
  if (!Number.isFinite(bytes)) return '—';
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  let value = bytes;
  let unit = 0;
  while (value >= 1024 && unit < units.length - 1) {
    value /= 1024;
    unit += 1;
  }
  return `${value.toFixed(unit > 2 ? 1 : 0)} ${units[unit]}`;
};

const formatUptime = seconds => {
  if (!Number.isFinite(seconds)) return '—';
  const days = Math.floor(seconds / 86400);
  const hours = Math.floor((seconds % 86400) / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  return `${days}d ${hours}h ${minutes}m`;
};

const formatRate = bytes => `${formatBytes(bytes)}/s`;
const processState = state => ({ R: 'Running', S: 'Sleeping', D: 'Waiting', Z: 'Zombie', I: 'Idle' })[state] || state;

const cards = computed(() => {
  if (!metrics.value) return [];
  return [
    {
      title: 'CPU',
      value: `${metrics.value.cpu.usage_percent}%`,
      detail: `${metrics.value.cpu.cores} logical cores`,
      percent: metrics.value.cpu.usage_percent,
      color: 'bg-violet-500',
    },
    {
      title: 'Memory',
      value: `${metrics.value.memory.usage_percent}%`,
      detail: `${formatBytes(metrics.value.memory.used_bytes)} of ${formatBytes(metrics.value.memory.total_bytes)}`,
      percent: metrics.value.memory.usage_percent,
      color: 'bg-cyan-500',
    },
    {
      title: 'Disk',
      value: `${metrics.value.disk.usage_percent}%`,
      detail: `${formatBytes(metrics.value.disk.available_bytes)} available`,
      percent: metrics.value.disk.usage_percent,
      color: 'bg-emerald-500',
    },
    {
      title: 'Download',
      value: formatRate(networkRates.value.down),
      detail: `${formatBytes(metrics.value.network.received_bytes)} total received`,
      percent: 0,
      color: 'bg-blue-500',
    },
    {
      title: 'Upload',
      value: formatRate(networkRates.value.up),
      detail: `${formatBytes(metrics.value.network.transmitted_bytes)} total sent`,
      percent: 0,
      color: 'bg-amber-500',
    },
    {
      title: 'Queued jobs',
      value: metrics.value.workloads.sidekiq.queued,
      detail: `${metrics.value.workloads.sidekiq.busy} workers busy`,
      percent: 0,
      color: 'bg-pink-500',
    },
  ];
});

const fetchMetrics = async () => {
  try {
    const response = await ResourceMonitorAPI.get();
    const next = response.data;
    if (previousNetwork) {
      const elapsed = Math.max((new Date(next.generated_at) - previousNetwork.time) / 1000, 0.1);
      networkRates.value = {
        down: Math.max(0, (next.network.received_bytes - previousNetwork.down) / elapsed),
        up: Math.max(0, (next.network.transmitted_bytes - previousNetwork.up) / elapsed),
      };
    }
    previousNetwork = { down: next.network.received_bytes, up: next.network.transmitted_bytes, time: new Date(next.generated_at) };
    metrics.value = next;
    error.value = '';
  } catch (fetchError) {
    error.value = fetchError?.response?.status === 403
      ? 'Administrator access is required.'
      : 'Resource data is temporarily unavailable.';
  } finally {
    loading.value = false;
  }
};

const runSpeedTest = async () => {
  speedModalOpen.value = true;
  speedTesting.value = true;
  speedResult.value = {};
  speedProgress.value = 0;
  liveGauge.value = 0;
  try {
    const phases = [
      { key: 'latency', label: 'Measuring ping and jitter', weight: 20 },
      { key: 'download', label: 'Testing sustained download', weight: 60 },
      { key: 'upload', label: 'Testing sustained upload', weight: 100 },
    ];
    for (const phase of phases) {
      speedStage.value = phase.label;
      const target = phase.weight - 3;
      const animation = window.setInterval(() => {
        speedProgress.value = Math.min(target, speedProgress.value + 1);
        liveGauge.value = Math.max(0, liveGauge.value + (Math.random() - 0.42) * 12);
      }, 120);
      const result = (await ResourceMonitorAPI.speedTest(phase.key)).data.speed_test;
      window.clearInterval(animation);
      if (!result.ok) throw new Error(result.error);
      speedResult.value[phase.key] = result;
      if (result.megabits_per_second) liveGauge.value = result.megabits_per_second;
      speedProgress.value = phase.weight;
    }
    speedStage.value = 'Test complete';
    speedProgress.value = 100;
  } catch (testError) {
    speedStage.value = `Test failed: ${testError.message}`;
  } finally {
    speedTesting.value = false;
  }
};

const closeSpeedTest = () => {
  if (!speedTesting.value) speedModalOpen.value = false;
};

onMounted(() => {
  fetchMetrics();
  refreshTimer = window.setInterval(fetchMetrics, 2000);
});

onBeforeUnmount(() => window.clearInterval(refreshTimer));
</script>

<template>
  <div class="flex h-full flex-col overflow-auto bg-n-background-1 p-6 lg:p-8">
    <header class="mb-7 flex flex-wrap items-center justify-between gap-4">
      <div>
        <p class="mb-1 text-sm font-medium text-n-slate-11">Agamagizh infrastructure</p>
        <h1 class="text-2xl font-semibold text-n-slate-12">Resource Monitor</h1>
        <p class="mt-1 text-sm text-n-slate-11">Live VPS health and core service status</p>
      </div>
      <div class="flex gap-2">
        <Button label="Test internet speed" icon="i-lucide-gauge" slate sm :is-loading="speedTesting" @click="runSpeedTest" />
        <Button label="Refresh now" icon="i-lucide-refresh-cw" slate sm :is-loading="loading" @click="fetchMetrics" />
      </div>
    </header>

    <div v-if="error" class="mb-5 rounded-xl border border-n-ruby-5 bg-n-ruby-2 p-4 text-sm text-n-ruby-11">
      {{ error }}
    </div>

    <template v-if="metrics">
      <section class="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
        <article v-for="card in cards" :key="card.title" class="rounded-2xl border border-n-weak bg-n-alpha-3 p-5 shadow-sm">
          <p class="text-sm font-medium text-n-slate-11">{{ card.title }}</p>
          <p class="mt-2 text-3xl font-semibold text-n-slate-12">{{ card.value }}</p>
          <div v-if="card.percent" class="mt-4 h-2 overflow-hidden rounded-full bg-n-alpha-2">
            <div :class="card.color" class="h-full rounded-full transition-all duration-500" :style="{ width: `${Math.min(card.percent, 100)}%` }" />
          </div>
          <p class="mt-3 text-xs text-n-slate-10">{{ card.detail }}</p>
        </article>
      </section>

      <section class="mt-5 grid gap-4 lg:grid-cols-2">
        <article class="rounded-2xl border border-n-weak bg-n-alpha-3 p-5 shadow-sm">
          <h2 class="font-semibold text-n-slate-12">System</h2>
          <dl class="mt-4 grid grid-cols-2 gap-4 text-sm">
            <div><dt class="text-n-slate-10">Uptime</dt><dd class="mt-1 font-medium text-n-slate-12">{{ formatUptime(metrics.system.uptime_seconds) }}</dd></div>
            <div><dt class="text-n-slate-10">Load average</dt><dd class="mt-1 font-medium text-n-slate-12">{{ metrics.system.load_average.join(' · ') }}</dd></div>
            <div><dt class="text-n-slate-10">Processes</dt><dd class="mt-1 font-medium text-n-slate-12">{{ metrics.system.process_count }}</dd></div>
            <div><dt class="text-n-slate-10">Database size</dt><dd class="mt-1 font-medium text-n-slate-12">{{ formatBytes(metrics.workloads.database.size_bytes) }}</dd></div>
          </dl>
          <p class="mt-4 text-xs text-n-slate-9">{{ metrics.system.hostname }} · {{ metrics.system.kernel }}</p>
        </article>

        <article class="rounded-2xl border border-n-weak bg-n-alpha-3 p-5 shadow-sm">
          <h2 class="font-semibold text-n-slate-12">Agamagizh services</h2>
          <ul class="mt-3 divide-y divide-n-weak">
            <li v-for="(service, key) in metrics.services" :key="key" class="flex items-center justify-between py-3 text-sm">
              <span class="text-n-slate-12">{{ service.label }}</span>
              <span class="flex items-center gap-2 font-medium" :class="service.healthy ? 'text-n-teal-11' : 'text-n-ruby-11'">
                <span class="size-2 rounded-full" :class="service.healthy ? 'bg-n-teal-9' : 'bg-n-ruby-9'" />
                {{ service.healthy ? 'Healthy' : 'Attention needed' }}
              </span>
            </li>
          </ul>
        </article>
      </section>

      <section class="mt-5 overflow-hidden rounded-2xl border border-n-weak bg-n-alpha-3 shadow-sm">
        <div class="border-b border-n-weak px-5 py-4"><h2 class="font-semibold text-n-slate-12">Task manager <span class="ml-2 text-xs font-normal text-n-slate-9">Top 20 by memory</span></h2></div>
        <div class="overflow-x-auto">
          <table class="w-full min-w-[650px] text-left text-sm">
            <thead class="bg-n-alpha-2 text-xs text-n-slate-10"><tr><th class="px-5 py-3">Process</th><th class="px-3 py-3">PID</th><th class="px-3 py-3">State</th><th class="px-3 py-3 text-right">CPU</th><th class="px-3 py-3 text-right">Memory</th><th class="px-5 py-3 text-right">Threads</th></tr></thead>
            <tbody class="divide-y divide-n-weak"><tr v-for="process in metrics.processes" :key="process.pid"><td class="px-5 py-2 font-medium text-n-slate-12">{{ process.name }}</td><td class="px-3 py-2 text-n-slate-10">{{ process.pid }}</td><td class="px-3 py-2 text-n-slate-10">{{ processState(process.state) }}</td><td class="px-3 py-2 text-right text-n-slate-12">{{ process.cpu_percent }}%</td><td class="px-3 py-2 text-right text-n-slate-12">{{ formatBytes(process.memory_bytes) }}</td><td class="px-5 py-2 text-right text-n-slate-12">{{ process.threads }}</td></tr></tbody>
          </table>
        </div>
      </section>

      <p class="mt-5 text-xs text-n-slate-9">Local metrics refresh every 2 seconds · Internet test runs only when requested · Last update {{ new Date(metrics.generated_at).toLocaleString() }}</p>
    </template>

    <div v-if="speedModalOpen" class="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4 backdrop-blur-sm" @click.self="closeSpeedTest">
      <section class="w-full max-w-xl rounded-3xl border border-n-weak bg-n-background-1 p-6 shadow-2xl">
        <div class="flex items-start justify-between">
          <div><p class="text-xs font-semibold uppercase tracking-widest text-n-slate-9">Agamagizh Network</p><h2 class="mt-1 text-xl font-semibold text-n-slate-12">Internet speed test</h2></div>
          <button class="rounded-lg p-2 text-n-slate-10 hover:bg-n-alpha-2" :disabled="speedTesting" @click="closeSpeedTest"><span class="i-lucide-x size-5" /></button>
        </div>

        <div class="mx-auto mt-8 flex size-56 flex-col items-center justify-center rounded-full border-[12px] border-n-alpha-2 bg-n-alpha-1 shadow-inner">
          <span class="text-5xl font-semibold tabular-nums text-n-slate-12">{{ Math.round(liveGauge) }}</span>
          <span class="mt-1 text-sm text-n-slate-10">Mbps</span>
          <span class="mt-3 text-xs font-medium text-n-slate-11">{{ speedStage }}</span>
        </div>

        <div class="mt-7 h-2 overflow-hidden rounded-full bg-n-alpha-2"><div class="h-full rounded-full bg-violet-500 transition-all duration-300" :style="{ width: `${speedProgress}%` }" /></div>

        <div class="mt-6 grid grid-cols-2 gap-3 sm:grid-cols-4">
          <div class="rounded-xl bg-n-alpha-2 p-3 text-center"><p class="text-xs text-n-slate-9">Ping</p><p class="mt-1 font-semibold text-n-slate-12">{{ speedResult?.latency?.ping_ms ?? '—' }} <small>ms</small></p></div>
          <div class="rounded-xl bg-n-alpha-2 p-3 text-center"><p class="text-xs text-n-slate-9">Jitter</p><p class="mt-1 font-semibold text-n-slate-12">{{ speedResult?.latency?.jitter_ms ?? '—' }} <small>ms</small></p></div>
          <div class="rounded-xl bg-n-alpha-2 p-3 text-center"><p class="text-xs text-n-slate-9">Download</p><p class="mt-1 font-semibold text-n-slate-12">{{ speedResult?.download?.megabits_per_second ?? '—' }} <small>Mbps</small></p></div>
          <div class="rounded-xl bg-n-alpha-2 p-3 text-center"><p class="text-xs text-n-slate-9">Upload</p><p class="mt-1 font-semibold text-n-slate-12">{{ speedResult?.upload?.megabits_per_second ?? '—' }} <small>Mbps</small></p></div>
        </div>

        <div v-if="!speedTesting && speedProgress === 100" class="mt-5 flex items-center justify-between text-xs text-n-slate-9">
          <span>Peak ↓ {{ speedResult.download.peak_megabits_per_second }} · Peak ↑ {{ speedResult.upload.peak_megabits_per_second }} Mbps</span>
          <Button label="Run again" icon="i-lucide-rotate-cw" slate sm @click="runSpeedTest" />
        </div>
        <p class="mt-4 text-center text-xs text-n-slate-9">Uses Cloudflare test endpoints · Up to 50 MB download and 25 MB upload per run</p>
      </section>
    </div>
  </div>
</template>
